/**
 * Self-schedule buffer field. Renders one of three presentations:
 *   - Read-only pill: "Buffer: 15m before + 15m after" + edit + eye.
 *   - Edit: two `NumberStepper` inputs (Before / After) + eye toggle.
 *   - Disabled (eye off): pill greyed; eye re-enables.
 *
 * The inner steppers reuse the library's `NumberStepper` atom — same
 * widget FormBuilder mounts for `type: 'numberStepper'` fields.
 */
import { useState } from 'react';
import { Box, Flex, IconButton } from '@chakra-ui/react';
import { NumberStepper } from '@kula-ai/components';
import { Edit02, Eye, EyeOff, Hourglass03 } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components';
import {
  BUFFER_MAX_MINUTES,
  BUFFER_MIN_MINUTES,
  BUFFER_STEP_MINUTES,
} from '../buffer';

export interface BufferFieldValue {
  before: number;
  after: number;
  enabled: boolean;
}

export interface BufferFieldProps {
  value: BufferFieldValue;
  onChange: (_next: BufferFieldValue) => void;
}

export function BufferField({ value, onChange }: BufferFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const { before, after, enabled } = value;

  const toggleEnabled = () => onChange({ ...value, enabled: !enabled });

  return (
    <Box
      w="100%"
      p="3"
      bg={enabled ? 'primary-25' : 'secondary-25'}
      border="1px solid"
      borderColor={enabled ? 'primary-100' : 'secondary-100'}
      borderRadius="md"
      opacity={enabled ? 1 : 0.7}
    >
      {isEditing && enabled ? (
        <Flex direction="column" gap="3">
          <Flex align="center" justify="space-between">
            <KText fontSize="sm" fontWeight="500" color="secondary-700">
              Meeting buffer
            </KText>
            <IconButton
              aria-label="Hide buffer"
              size="xs"
              variant="ghost"
              icon={<Eye width="16" height="16" />}
              onClick={toggleEnabled}
            />
          </Flex>
          <Flex gap="3">
            <Flex direction="column" gap="1" flex="1">
              <KText fontSize="xs" color="secondary-600">
                Before meeting buffer
              </KText>
              <NumberStepper
                value={before}
                min={BUFFER_MIN_MINUTES}
                max={BUFFER_MAX_MINUTES}
                step={BUFFER_STEP_MINUTES}
                unitLabel="mins"
                onChange={(n) => onChange({ ...value, before: n })}
              />
            </Flex>
            <Flex direction="column" gap="1" flex="1">
              <KText fontSize="xs" color="secondary-600">
                After meeting buffer
              </KText>
              <NumberStepper
                value={after}
                min={BUFFER_MIN_MINUTES}
                max={BUFFER_MAX_MINUTES}
                step={BUFFER_STEP_MINUTES}
                unitLabel="mins"
                onChange={(n) => onChange({ ...value, after: n })}
              />
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <Flex align="center" justify="space-between" gap="2">
          <Flex align="center" gap="2" minW={0}>
            <Hourglass03 width="16" height="16" />
            <KText fontSize="sm" color="secondary-700" isTruncated>
              {enabled
                ? `Buffer: ${before}m before + ${after}m after`
                : `Buffer disabled (${before}m / ${after}m)`}
            </KText>
          </Flex>
          <Flex align="center" gap="1">
            <IconButton
              aria-label="Edit buffer"
              size="xs"
              variant="ghost"
              icon={<Edit02 width="16" height="16" />}
              isDisabled={!enabled}
              onClick={() => setIsEditing(true)}
            />
            <IconButton
              aria-label={enabled ? 'Hide buffer' : 'Show buffer'}
              size="xs"
              variant="ghost"
              icon={
                enabled ? (
                  <Eye width="16" height="16" />
                ) : (
                  <EyeOff width="16" height="16" />
                )
              }
              onClick={toggleEnabled}
            />
          </Flex>
        </Flex>
      )}
    </Box>
  );
}
